import { useMemo, useState } from "react";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { achColor, achievement, fmtDate, todayISO, useStore } from "../store";
import { useCrud } from "../useCrud";
import {
  Btn, Card, ConfirmDelete, Empty, Field, IconBtn, Modal, PageHead, Pill, Select, Table, Td, inputCls, statusTone,
} from "../ui";

const STATUS = ["Pending", "In Progress", "Done"];
const YN = ["Yes", "No"];
const blank = () => ({ date: todayISO(), personId: "", task: "", target: "", actual: "", status: "Pending", reviewed: "No", tracked: "No", remark: "" });
const validate = (f) => {
  const e = {};
  if (!f.date) e.date = "Date is required";
  if (!f.personId) e.personId = "Select a person";
  if (!f.task?.trim()) e.task = "Task is required";
  if (f.target === "" || !(Number(f.target) > 0)) e.target = "Target must be greater than 0";
  if (f.actual === "" || Number(f.actual) < 0 || isNaN(Number(f.actual))) e.actual = "Actual must be 0 or more";
  return e;
};

const TaskForm = ({ c, activePeople }) => (
  <form onSubmit={c.save} className="grid grid-cols-1 gap-4 sm:grid-cols-2" data-testid="task-form">
    <Field label="Date *" error={c.errors.date}><input type="date" className={inputCls} value={c.form.date} onChange={c.change("date")} data-testid="task-date-input" /></Field>
    <Field label="Person *" error={c.errors.personId}>
      <Select options={activePeople} placeholder={activePeople.length ? "Select person" : "No active people — add in Settings"} value={c.form.personId} onChange={c.change("personId")} data-testid="task-person-select" />
    </Field>
    <Field label="Task *" error={c.errors.task} className="sm:col-span-2"><input className={inputCls} value={c.form.task} onChange={c.change("task")} placeholder="e.g. Cold calls" data-testid="task-name-input" /></Field>
    <Field label="Target *" error={c.errors.target}><input type="number" min="0" step="any" className={inputCls} value={c.form.target} onChange={c.change("target")} data-testid="task-target-input" /></Field>
    <Field label="Actual *" error={c.errors.actual}><input type="number" min="0" step="any" className={inputCls} value={c.form.actual} onChange={c.change("actual")} data-testid="task-actual-input" /></Field>
    <Field label="Status"><Select options={STATUS} value={c.form.status} onChange={c.change("status")} data-testid="task-status-select" /></Field>
    <div className="grid grid-cols-2 gap-4">
      <Field label="Reviewed"><Select options={YN} value={c.form.reviewed} onChange={c.change("reviewed")} data-testid="task-reviewed-select" /></Field>
      <Field label="Tracked"><Select options={YN} value={c.form.tracked} onChange={c.change("tracked")} data-testid="task-tracked-select" /></Field>
    </div>
    <Field label="Remark" className="sm:col-span-2"><textarea rows={2} className={inputCls} value={c.form.remark} onChange={c.change("remark")} data-testid="task-remark-input" /></Field>
    <div className="flex justify-end gap-2 sm:col-span-2">
      <Btn type="button" variant="ghost" onClick={c.close} data-testid="task-cancel-btn">Cancel</Btn>
      <Btn type="submit" data-testid="task-save-btn">Save task</Btn>
    </div>
  </form>
);

const TaskRow = ({ t, name, c }) => {
  const a = achievement(t);
  return (
    <tr className="transition-colors hover:bg-white/[0.02]" data-testid={`task-row-${t.id}`}>
      <Td className="whitespace-nowrap">{fmtDate(t.date)}</Td>
      <Td className="whitespace-nowrap font-medium text-zinc-100">{name}</Td>
      <Td className="max-w-[220px]">{t.task}</Td>
      <Td className="font-mono-nums">{t.target}</Td>
      <Td className="font-mono-nums">{t.actual}</Td>
      <Td className={`font-mono-nums font-bold ${achColor(a)}`} data-testid="task-achievement">{a == null ? "—" : `${Math.round(a)}%`}</Td>
      <Td><Pill tone={statusTone(t.status)}>{t.status}</Pill></Td>
      <Td><Pill tone={t.reviewed === "Yes" ? "green" : "zinc"}>{t.reviewed}</Pill></Td>
      <Td><Pill tone={t.tracked === "Yes" ? "green" : "zinc"}>{t.tracked}</Pill></Td>
      <Td className="max-w-[200px] text-zinc-500">{t.remark || "—"}</Td>
      <Td className="whitespace-nowrap">
        <IconBtn onClick={() => c.openEdit(t)} data-testid={`task-edit-${t.id}`}><Pencil className="h-4 w-4" /></IconBtn>
        <IconBtn onClick={() => c.setDelId(t.id)} className="hover:text-rose-400" data-testid={`task-delete-${t.id}`}><Trash2 className="h-4 w-4" /></IconBtn>
      </Td>
    </tr>
  );
};

export default function DailyTask() {
  const { tasks, people } = useStore();
  const c = useCrud("tasks", blank, validate, "Task");
  const [filter, setFilter] = useState(todayISO());
  const activePeople = people.filter((p) => p.status === "Active" || p.id === c.form?.personId).map((p) => ({ value: p.id, label: p.name }));
  const nameOf = (t) => people.find((p) => p.id === t.personId)?.name || t.personName || "—";
  const rows = useMemo(() => tasks.filter((t) => !filter || t.date === filter).sort((a, b) => b.date.localeCompare(a.date)), [tasks, filter]);
  const save = (e) => c.save(e, (f) => ({ ...f, personName: people.find((p) => p.id === f.personId)?.name }));

  return (
    <div>
      <PageHead title="Daily Task" sub="Assign and track each person's work for the day.">
        <div className="flex items-center gap-2">
          <input type="date" className={`${inputCls} w-auto`} value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="task-date-filter" />
          {filter && <Btn variant="ghost" onClick={() => setFilter("")} data-testid="task-filter-clear-btn">All dates</Btn>}
        </div>
        <Btn onClick={() => c.openNew({ date: filter || todayISO() })} data-testid="add-task-btn"><Plus className="h-4 w-4" />Add Task</Btn>
      </PageHead>
      <Card className="animate-rise [animation-delay:80ms]">
        {rows.length ? (
          <Table head={["Date", "Person", "Task", "Target", "Actual", "Achievement", "Status", "Reviewed", "Tracked", "Remark", ""]}>
            {rows.map((t) => <TaskRow key={t.id} t={t} name={nameOf(t)} c={c} />)}
          </Table>
        ) : (
          <Empty testid="tasks-empty" title={filter ? `No tasks for ${fmtDate(filter)}` : "No tasks yet"} hint="Click “Add Task” to assign today's work." />
        )}
      </Card>
      <Modal open={!!c.form} onClose={c.close} title={c.editId ? "Edit Task" : "Add Task"} testid="task-modal">
        {c.form && <TaskForm c={{ ...c, save }} activePeople={activePeople} />}
      </Modal>
      <ConfirmDelete open={!!c.delId} onCancel={() => c.setDelId(null)} onConfirm={c.confirmDelete} what="this task" />
    </div>
  );
}
